import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Alert as MUIAlert, Fade } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faExclamationCircle,
  faCheckCircle,
  faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';

const StyledAlert = styled(MUIAlert)`
  border-radius: 0;
  border: 1px solid;
  font-family: 'Whitney Medium';
  font-size: 1.125rem;
  line-height: normal;

  a {
    font-family: 'Whitney Bold';
  }

  &.MuiAlert-colorError {
    background-color: #f2d2d5;
    border-color: #edc0c4;
    color: #631017;
  }

  &.MuiAlert-colorSuccess {
    background-color: #cde9da;
    border-color: #b9e1cb;
    color: #034c25;
  }

  &.MuiAlert-colorInfo {
    background-color: #cce2eb;
    border-color: #b8d6e3;
    color: #003851;
  }

  &.MuiAlert-colorWarning {
    background-color: #fff4da;
    border-color: #ffefcb;
    color: #856724;

    a {
      color: #856724;

      &:hover {
        color: #644e1b;
      }
    }
  }

  .MuiAlert-action {
    padding: 4px 0 0 0;
  }
`;

export const Alert = ({
  variant,
  message,
  onClose = () => {},
  withoutFade,
  sx,
}) => {
  const [alertVisibility, setAlertVisibility] = useState(true);

  useEffect(() => {
    if (!alertVisibility && !withoutFade) {
      setTimeout(() => {
        onClose();
      }, 400);
    }
  }, [alertVisibility]);

  if (withoutFade) {
    if (!alertVisibility) return null;

    return (
      <StyledAlert
        severity={variant}
        onClose={() => { setAlertVisibility(false); onClose(); }}
        sx={sx}
        iconMapping={{
          success: <FontAwesomeIcon icon={faCheckCircle} color="#034c25" />,
          error: <FontAwesomeIcon icon={faExclamationCircle} color="#631017" />,
          info: <FontAwesomeIcon icon={faCircleInfo} color="#003851" />,
          warning: (
            <FontAwesomeIcon icon={faExclamationTriangle} color="#ffc646" />
          ),
        }}
      >
        <div>{message}</div>
      </StyledAlert>
    );
  }

  return (
    <Fade
      in={alertVisibility}
      timeout={{ enter: 500, exit: 500 }}
      addEndListener={() => {
        setTimeout(() => {
          setAlertVisibility(false);
        }, 5000);
      }}
    >
      <StyledAlert
        severity={variant}
        onClose={onClose}
        sx={sx}
        iconMapping={{
          success: <FontAwesomeIcon icon={faCheckCircle} color="#034c25" />,
          error: <FontAwesomeIcon icon={faExclamationCircle} color="#631017" />,
          warning: (
            <FontAwesomeIcon icon={faExclamationTriangle} color="#ffc646" />
          ),
        }}
      >
        <div>{message}</div>
      </StyledAlert>
    </Fade>
  );
};

Alert.propTypes = {
  message: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.object.isRequired,
  ]),
  onClose: PropTypes.func.isRequired,
  variant: PropTypes.string,
};
Alert.defaultProps = {
  variant: 'success',
};
